// src/PrivacyPolicy.js
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from './Footer';

const PrivacyPolicy = () => {
  return (
    <div className="policy-page">
      {/* Header */}
      <section className="policy-hero">
        <div className="container">
          <motion.div
            className="policy-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="back-link-wrapper">
              <Link to="/" className="back-link">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></svg>
                Back to Home
              </Link>
            </div>
            <h1 className="policy-title">Privacy Policy for Panda</h1>
            <p className="policy-subtitle">
              Effective Date: August 9, 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="policy-content">
        <div className="container">
          <motion.div
            className="policy-article"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <p>
              Thank you for using Panda ("the App"), your personal AI phone operator. This Privacy Policy explains what information we collect, how we use and share it, and your choices regarding your information.
            </p>

            <h2>1. Information We Collect</h2>
            <p>
              To function as an on-device AI agent, Panda requires access to certain information. We are committed to transparency and only collecting what is necessary to provide and improve the service.
            </p>

            <h3>a) Screen Content (via Accessibility Service)</h3>
            <p>
              To understand your requests and operate your phone's user interface, the app uses the Android Accessibility Service. This service allows Panda to collect:
            </p>
            <ul>
              <li><strong>UI Element Information:</strong> Text, content descriptions, and resource IDs of elements on your screen.</li>
              <li><strong>Screen Structure:</strong> The hierarchical layout of your screen (as XML data).</li>
              <li><strong>Screenshots:</strong> The app has the capability to take screenshots to provide visual context to its AI models.</li>
            </ul>
            <p>
              This information is collected to understand the current state of your device to perform actions like tapping, swiping, and typing on your behalf.
            </p>

            <h3>b) Audio Information (via Microphone Permission)</h3>
            <p>The app requires the <code>RECORD_AUDIO</code> permission to:</p>
            <ul>
              <li><strong>Process Voice Commands:</strong> When you speak a command, the audio is processed to convert it into text.</li>
              <li><strong>Wake Word Detection:</strong> The app listens for the "Panda" wake word to activate. This processing is done <em>entirely on your device</em> by the OpenWakeWord engine; your ambient audio is not sent to the cloud for wake word detection.</li>
            </ul>

            <h3>c) User Instructions & Conversations</h3>
            <p>
              We collect the text of your typed or spoken commands and the history of your conversation with Panda during a session to understand your goals and provide relevant responses.
            </p>

            <h3>d) Installed Applications Information</h3>
            <p>
              The app uses the <code>QUERY_ALL_PACKAGES</code> permission to get a list of your installed applications. This information is used solely to identify apps by name so Panda can open them for you more quickly and reliably.
            </p>

            <h3>e) User-Created Memories (On-Device Storage)</h3>
            <ul>
              <li><strong>Automatic Extraction:</strong> Facts may be extracted from your conversations to personalize your experience (e.g., remembering your brother's name if you mention it). This analysis is performed by Google's Gemini AI.</li>
              <li><strong>Manual Creation:</strong> You can manually add facts in the "My Memories" section.</li>
            </ul>
            <p>
              All extracted and created memories are stored in a secure, private database on your device. <em>They are not accessible by other applications.</em>
            </p>

            <h3>f) Location Data</h3>
            <p>
              The app requests the following location permissions:
            </p>
            <ul>
              <li><strong>Precise Location (<code>ACCESS_FINE_LOCATION</code>) and Approximate Location (<code>ACCESS_COARSE_LOCATION</code>):</strong> Your device's location is accessed to provide context-aware responses (e.g., answering "What's the weather near me?") and to enable location-based automation triggers you create.</li>
              <li><strong>Background Location (<code>ACCESS_BACKGROUND_LOCATION</code>):</strong> If you set up location-based triggers (e.g., "Run this task when I arrive home"), the app requires background location access to monitor geofence boundaries even when the app is not in the foreground. This permission is only used to evaluate the trigger conditions you have explicitly configured.</li>
            </ul>
            <p>
              Your location data is <strong>not sold or shared</strong> with third parties for advertising. It may be included in the context sent to Google's Gemini AI models solely to fulfill your specific request (e.g., fetching local weather). Location coordinates used for geofence triggers are stored only on your device.
            </p>

            <h3>g) Unique User Identifier</h3>
            <p>
              The app generates a random, anonymous Unique User ID (UUID) that is stored on your device. This helps in managing your memories and sessions without using any personal identifiers like your name or email.
            </p>

            <h3>h) Contacts</h3>
            <p>
              When you ask Panda to look up, message, or call someone, the app accesses your contacts using the <code>READ_CONTACTS</code> permission to find names, phone numbers, and email addresses. If you ask Panda to save a new contact, it uses <code>WRITE_CONTACTS</code> to create it. Contact data is accessed on-demand only, is not stored in any external database, and is not shared with third parties except when included as context in a Gemini AI request you explicitly make (e.g., "Send a message to John").
            </p>

            <h3>i) SMS & Text Messages</h3>
            <p>
              With the <code>READ_SMS</code> permission, Panda can read your SMS inbox, specific threads, and search your messages to answer questions or take actions you request. With the <code>SEND_SMS</code> permission, it can send text messages on your behalf. Message content that is relevant to your command may be sent to Google's Gemini AI as part of the prompt context, solely to fulfill your request. Raw message content is never stored on our servers.
            </p>

            <h3>j) Call Log & Phone Calls</h3>
            <p>
              The <code>READ_CALL_LOG</code> permission lets Panda retrieve your recent and missed calls to answer queries like "Who called me today?". The <code>CALL_PHONE</code> permission allows Panda to initiate phone calls when you ask. Call log data is read on-demand and may be included in Gemini AI context only to fulfill your specific request. It is not stored on external servers.
            </p>

            <h3>k) Calendar Events</h3>
            <p>
              The <code>READ_CALENDAR</code> permission lets Panda read your upcoming events to answer schedule-related questions. The <code>WRITE_CALENDAR</code> permission allows it to create new events when you ask. Calendar data is accessed only in response to your commands and may be sent to Google's Gemini AI as context for your specific request. It is not stored on our servers.
            </p>

            <h3>l) Photos & Videos</h3>
            <p>
              When you ask Panda about your media (e.g., "Show me my recent photos"), it accesses photo and video metadata — file names, dates, sizes, and durations — using <code>READ_MEDIA_IMAGES</code> and <code>READ_MEDIA_VIDEO</code> (or <code>READ_EXTERNAL_STORAGE</code> on older Android versions). Only metadata is read unless you explicitly direct Panda to use a specific image or video in a task. Media files and metadata are not uploaded to any external server except when you specifically request an action that requires it.
            </p>

            <h3>m) Notifications</h3>
            <p>
              With your explicit permission via the Android Notification Listener, Panda can read your currently active notifications — including the app name, notification title, and body text — to answer queries like "What are my unread notifications?". Notification content may be included in Gemini AI context to fulfill your request. It is not stored on our servers and is only read when you ask.
            </p>

            <h3>n) Device Status (Battery, Wi-Fi, Bluetooth)</h3>
            <p>
              Panda can read device status information — including battery level and charge state, Wi-Fi connection name and signal strength, and paired Bluetooth device names and addresses — to answer questions about your device. This information is read locally and may be included in an AI request you make. It is not transmitted to our servers.
            </p>

            <h3>o) Clipboard</h3>
            <p>
              Panda can read and write your device clipboard when you explicitly ask it to (e.g., "Copy this to my clipboard" or "What's in my clipboard?"). Clipboard content is only accessed on-demand and is not stored or transmitted to external services unless you direct Panda to use that content in a task.
            </p>

            <h3>p) Alarms & Audio Settings</h3>
            <p>
              Panda can create alarms and query the next scheduled alarm via the Android AlarmManager. It can also read and adjust device volume levels (media, ringtone, notification, alarm) when you ask. These operations are performed locally on your device and no data from these actions is transmitted to external servers.
            </p>

            <h3>q) On-Device Files (Notes, Tasks, Results)</h3>
            <p>
              To support multi-step tasks, Panda reads and writes simple files (notes, to-do lists, task results) to a private, sandboxed directory on your device. These files are not accessible by other apps and are not uploaded to any server. You can clear them by uninstalling the app.
            </p>

            <h3>r) Third-Party Service Connections (Composio)</h3>
            <p>
              Panda supports optional connections to third-party services (such as Google Calendar, Gmail, and others) via the Composio integration platform. If you choose to connect these services, Panda may send instructions to Composio on your behalf to execute tasks. The data exchanged is limited to what is necessary to complete your specific request. Your credentials are stored by Composio under their own Privacy Policy; Panda does not directly store OAuth tokens or passwords for third-party services.
            </p>

            <h3>s) Log Data for Debugging</h3>
            <p>
              To diagnose and fix issues, we log some interaction data with our third-party AI services. This data is sent to Google Firebase Firestore and includes the AI prompt, the AI's response, the AI model used, and success/error status. This log data is not linked to your personal identity.
            </p>

            <h2>2. How We Use Your Information</h2>
            <ul>
              <li><strong>To Provide Core Functionality:</strong> To understand your commands and automate actions on your device.</li>
              <li><strong>To Personalize Your Experience:</strong> To use your stored memories to provide more context-aware and helpful responses.</li>
              <li><strong>To Improve the Service:</strong> To analyze anonymized log data to debug issues and enhance the app's performance and reliability.</li>
            </ul>

            <h2>3. Data Sharing and Third-Party Services</h2>
            <p>Panda is a complex AI system and relies on a few specialized third-party services to function. We do not sell your data.</p>
            <ul>
              <li><strong>Google (Gemini AI Models):</strong> Your conversations, screen content (XML or screenshots), and text from new memories are sent to Google's Generative AI services for processing, reasoning, and analysis. This is essential for the AI to understand context and generate responses. Your data is subject to Google's Privacy Policy.</li>
              <li><strong>Google (Firebase Firestore):</strong> Anonymized log data of AI interactions is stored in Google's Firestore for debugging and service improvement, as described in section 1(s).</li>
              <li><strong>Tavily (Search API):</strong> If a task requires web research, a search query derived from your instruction is sent to the Tavily Search API to find information. This is subject to Tavily's Privacy Policy.</li>
              <li><strong>OpenWakeWord (Wake Word Detection):</strong> As stated in section 1(b), wake word detection is processed <em>100% on your device</em> by the open-source OpenWakeWord engine. No audio is sent to any external server for wake word detection.</li>
              <li><strong>Composio (Third-Party Service Integrations):</strong> If you connect optional third-party services (e.g., Gmail, Google Calendar), task instructions are sent to Composio to execute actions on your behalf. Your connected service credentials are managed by Composio under their Privacy Policy. Panda does not retain these credentials.</li>
            </ul>

            <h2>4. Your Control and Choices</h2>
            <p>You are in control of your data.</p>
            <ul>
              <li><strong>Managing Memories:</strong> You can view, add, and permanently delete any or all of your memories at any time from the "My Memories" screen within the app.</li>
              <li><strong>Revoking Permissions:</strong> You can revoke any permission — including Accessibility Service, Microphone, Contacts, SMS, Call Log, Calendar, Media, Location, and Notification Listener — at any time through your device's Android Settings. Revoking a permission will disable the corresponding Panda capability but will not affect other features.</li>
              <li><strong>Disconnecting Third-Party Services:</strong> You can disconnect any Composio-linked service (e.g., Gmail, Google Calendar) from within the app's Connectors settings at any time.</li>
            </ul>

            <h2>5. Changes to This Privacy Policy</h2>
            <p>
              We may update this policy from time to time. We will notify you of any changes by posting the new policy in this section of the app.
            </p>

            <h2>6. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us at:{' '}
              <a href="mailto:support@heypanda.org" className="policy-link">
                support@heypanda.org
              </a>
            </p>
          </motion.div>
        </div>
      </section>



      <Footer />

      <style jsx>{`
        .policy-page {
          min-height: 100vh;
          background: 
            radial-gradient(circle at 20% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(6, 182, 212, 0.05) 0%, transparent 50%),
            var(--gradient-dark);
          background-attachment: fixed;
          color: var(--text-primary);
        }

        .back-link-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 32px;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          border-radius: 100px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          color: var(--text-secondary);
          text-decoration: none;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          backdrop-filter: blur(8px);
        }

        .back-link:hover {
          color: white;
          background: rgba(255,255,255,0.12);
          border-color: rgba(255,255,255,0.25);
          transform: translateX(-4px);
        }

        .back-link svg {
          transition: transform 0.3s ease;
        }

        .back-link:hover svg {
          transform: translateX(-3px);
        }

        .policy-hero {
          padding: 120px 0 60px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .policy-header {
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        .policy-title {
          font-size: 3.5rem;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .policy-subtitle {
          font-size: 1.1rem;
          color: var(--text-secondary);
        }

        .policy-content {
          padding: 60px 0;
        }

        .policy-article {
          max-width: 800px;
          margin: 0 auto;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 40px;
          backdrop-filter: blur(10px);
        }

        .policy-article h2 {
          font-size: 1.75rem;
          color: var(--primary-color);
          margin-top: 40px;
          margin-bottom: 20px;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .policy-article h2:first-child {
          margin-top: 0;
        }

        .policy-article h3 {
          font-size: 1.25rem;
          color: var(--text-primary);
          margin-top: 24px;
          margin-bottom: 12px;
        }

        .policy-article p {
          font-size: 1.1rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 16px;
        }

        .policy-article ul {
          margin-bottom: 24px;
          padding-left: 24px;
        }

        .policy-article li {
          font-size: 1.1rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .policy-article li strong {
          color: var(--text-primary);
        }

        .policy-article code {
          background: rgba(255, 255, 255, 0.1);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          color: var(--text-primary);
        }

        .policy-link {
          color: var(--primary-color);
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .policy-link:hover {
          color: var(--text-primary);
          text-decoration: underline;
        }

        @media (max-width: 768px) {
          .policy-hero {
            padding: 80px 0 40px;
          }
          .policy-title {
            font-size: 2.5rem;
          }
          .policy-article {
            padding: 24px;
          }
          .policy-article h2 {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div >
  );
};

export default PrivacyPolicy;